import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Paper, Snackbar, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Tooltip,
} from "@mui/material";
import Add from "@mui/icons-material/Add";
import Close from "@mui/icons-material/Close";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import Save from "@mui/icons-material/Save";
import api from "../api/axios";
import { mandantUrl, useBusinessContext } from "../BusinessContext";
import PageHeader from "../components/PageHeader";
import LoadingIndicator from '../components/LoadingIndicator';
import LoadError from '../components/LoadError';


export default function Kategorien() {
  const { mandantId } = useBusinessContext();
  const scopeState = useRef(null);
  const requests = useRef(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ kuerzel: "", beschreibung: "" });
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState("");
  const [message, setMessage] = useState({ text: "", severity: "success" });

  const load = useCallback(async () => {
    const scope = scopeState.current;
    if (!mandantId || !scope?.active || scope.mandantId !== mandantId) return false;
    const job = ++requests.current;
    setLoading(true); setLoadError('');
    try {
      const response = await api.get(mandantUrl("/kategorien", mandantId));
      if (!scope.active || job !== requests.current) return false;
      setItems(Array.isArray(response.data) ? response.data : []);
      return true;
    } catch (error) {
      if (scope.active && job === requests.current) {
        setLoadError(error.response?.data?.error || 'Kategorien konnten nicht geladen werden.');
      }
      return false;
    } finally { if (scope.active && job === requests.current) setLoading(false); }
  }, [mandantId]);
  useEffect(() => {
    const scope = { mandantId, active: true };
    scopeState.current = scope;
    setItems([]); setDialogOpen(false); setSelected(null); setSaving(false); setLoading(false); setLoadError('');
    setMessage({ text: "", severity: "success" });
    load();
    return () => { scope.active = false; };
  }, [load, mandantId]);

  const openDialog = (item = null) => {
    setSelected(item);
    setForm({ kuerzel: item?.kuerzel || "", beschreibung: item?.beschreibung || "" });
    setDialogError("");
    setDialogOpen(true);
  };
  const closeDialog = () => { setDialogOpen(false); setSelected(null); };

  const save = async () => {
    if (saving || !mandantId) return;
    const scope = scopeState.current;
    setSaving(true);
    setDialogError("");
    try {
      if (selected) await api.put(mandantUrl(`/kategorien/${selected.id}`, mandantId), form);
      else await api.post(mandantUrl("/kategorien", mandantId), form);
      if (!scope.active) return;
      closeDialog();
      if (await load()) setMessage({ text: "Kategorie erfolgreich gespeichert.", severity: "success" });
    } catch (error) {
      if (scope.active) setDialogError(error.response?.data?.error || "Die Kategorie konnte nicht gespeichert werden.");
    } finally {
      if (scope.active) setSaving(false);
    }
  };

  const remove = async (item) => {
    const scope = scopeState.current;
    if (!window.confirm(`Soll die Kategorie ${item.kuerzel} wirklich gelöscht werden?`)) return;
    try {
      await api.delete(mandantUrl(`/kategorien/${item.id}`, mandantId));
      if (!scope.active) return;
      if (await load()) setMessage({ text: "Kategorie erfolgreich gelöscht.", severity: "success" });
    } catch (error) {
      if (scope.active) setMessage({ text: error.response?.data?.error || "Die Kategorie konnte nicht gelöscht werden.", severity: "error" });
    }
  };

  return (
    <Box>
      <PageHeader
        title="Kategorien"
        action={<Button variant="contained" size="small" startIcon={<Add />} disabled={!mandantId} onClick={() => openDialog()}>Hinzufügen</Button>}
      />
      {!mandantId && <Alert severity="info">Bitte einen Mandanten auswählen.</Alert>}
      {loading && <LoadingIndicator label="Kategorien werden geladen …" />}
      {loadError && <LoadError onRetry={load}>{loadError}</LoadError>}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead><TableRow><TableCell>Kürzel</TableCell><TableCell>Beschreibung</TableCell><TableCell align="right" sx={{ whiteSpace: "nowrap" }}>Aktionen</TableCell></TableRow></TableHead>
          <TableBody>
            {!loading && !loadError && mandantId && items.length === 0 ? <TableRow><TableCell colSpan={3} align="center">Keine Kategorien.</TableCell></TableRow> : items.map((item) => (
              <TableRow key={item.id} hover>
                <TableCell>{item.kuerzel}</TableCell><TableCell>{item.beschreibung}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  <Tooltip title="Bearbeiten"><IconButton size="small" color="primary" aria-label={`Kategorie ${item.kuerzel} bearbeiten`} onClick={() => openDialog(item)}><Edit fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Löschen"><IconButton size="small" color="error" aria-label={`Kategorie ${item.kuerzel} löschen`} onClick={() => remove(item)}><Delete fontSize="small" /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={dialogOpen} onClose={saving ? undefined : closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{selected ? "Kategorie bearbeiten" : "Kategorie hinzufügen"}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", pt: "12px !important" }}>
          {dialogError && <Alert severity="error">{dialogError}</Alert>}
          <TextField size="small" label="Kürzel" value={form.kuerzel} onChange={(event) => setForm((current) => ({ ...current, kuerzel: event.target.value }))} slotProps={{ htmlInput: { "aria-label": "Kürzel" } }} required />
          <TextField size="small" label="Beschreibung" value={form.beschreibung} onChange={(event) => setForm((current) => ({ ...current, beschreibung: event.target.value }))} slotProps={{ htmlInput: { "aria-label": "Beschreibung" } }} multiline minRows={2} required />
        </DialogContent>
        <DialogActions>
          <Button color="secondary" startIcon={<Close />} onClick={closeDialog} disabled={saving}>Abbrechen</Button>
          <Button variant="contained" startIcon={<Save />} onClick={save} disabled={saving || !form.kuerzel.trim() || !form.beschreibung.trim()}>Speichern</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(message.text)}
        autoHideDuration={4000}
        onClose={() => setMessage((current) => ({ ...current, text: "" }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={message.severity} variant="filled">{message.text}</Alert>
      </Snackbar>
    </Box>
  );
}
